import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  // Tambahkan komponen Table yang dibutuhkan
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloudIcon from '@mui/icons-material/Cloud';
import ApiIcon from '@mui/icons-material/Api';
import AddIcon from '@mui/icons-material/Add'; // Tambahkan import untuk AddIcon
import api from '../../../services/api';

const IntegrationSettings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: false,
    sms: false
  });
  
  // Storage settings
  const [storageSettings, setStorageSettings] = useState({
    provider: 'local',
    config: {}
  });
  
  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  
  // Dialog states
  const [openAddApiKeyDialog, setOpenAddApiKeyDialog] = useState(false);
  const [openDeleteApiKeyDialog, setOpenDeleteApiKeyDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Form states
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [apiKeyFormData, setApiKeyFormData] = useState({
    name: '',
    permissions: ['read']
  });
  
  const storageProviders = [
    { value: 'local', label: 'Local Storage' },
    { value: 'google_drive', label: 'Google Drive' },
    { value: 'gcs', label: 'Google Cloud Storage' },
    { value: 's3', label: 'Amazon S3' }
  ];
  
  const apiPermissions = [
    { value: 'read', label: 'Read Only' },
    { value: 'write', label: 'Read & Write' },
    { value: 'admin', label: 'Admin Access' }
  ];

  // Wrap fetch functions in useCallback
  const fetchNotificationSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/notifications');
      setNotificationSettings(response.data.settings || {
        email: true,
        push: false,
        sms: false
      });
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat pengaturan notifikasi',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStorageSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/storage');
      setStorageSettings(response.data.settings || {
        provider: 'local',
        config: {}
      });
    } catch (error) {
      console.error('Error fetching storage settings:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat pengaturan penyimpanan',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/api-keys');
      setApiKeys(response.data.apiKeys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat API keys',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Now the useEffect with stable dependencies
  useEffect(() => {
    if (tabValue === 0) {
      fetchNotificationSettings();
    } else if (tabValue === 1) {
      fetchStorageSettings();
    } else if (tabValue === 2) {
      fetchApiKeys();
    }
  }, [tabValue, fetchNotificationSettings, fetchStorageSettings, fetchApiKeys]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSaveNotificationSettings = async () => {
    setLoading(true);
    try {
      await api.post('/admin/settings/notifications', { settings: notificationSettings });
      setAlert({
        open: true,
        message: 'Pengaturan notifikasi berhasil disimpan',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setAlert({
        open: true,
        message: 'Gagal menyimpan pengaturan notifikasi',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStorageSettings = async () => {
    setLoading(true);
    try {
      await api.post('/admin/settings/storage', { settings: storageSettings });
      setAlert({
        open: true,
        message: 'Pengaturan penyimpanan berhasil disimpan',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving storage settings:', error);
      setAlert({
        open: true,
        message: 'Gagal menyimpan pengaturan penyimpanan',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddApiKeyDialog = () => {
    setApiKeyFormData({
      name: '',
      permissions: ['read']
    });
    setOpenAddApiKeyDialog(true);
  };

  const handleOpenDeleteApiKeyDialog = (apiKey) => {
    setSelectedApiKey(apiKey);
    setOpenDeleteApiKeyDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddApiKeyDialog(false);
    setOpenDeleteApiKeyDialog(false);
    setSelectedApiKey(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApiKeyFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (e) => {
    setApiKeyFormData(prev => ({
      ...prev,
      permissions: e.target.value
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleStorageProviderChange = (e) => {
    setStorageSettings(prev => ({
      ...prev,
      provider: e.target.value
    }));
  };

  const handleCreateApiKey = async () => {
    setDialogLoading(true);
    try {
      const response = await api.post('/admin/api-keys', apiKeyFormData);
      setNewApiKey(response.data.apiKey);
      setApiKeys([...apiKeys, response.data.apiKey]);
      setAlert({
        open: true,
        message: 'API key berhasil dibuat',
        severity: 'success'
      });
      // Don't close dialog yet, show the new API key
      setShowApiKey(true);
    } catch (error) {
      console.error('Error creating API key:', error);
      setAlert({
        open: true,
        message: 'Gagal membuat API key',
        severity: 'error'
      });
      handleCloseDialog();
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    setDialogLoading(true);
    try {
      await api.delete(`/admin/api-keys/${selectedApiKey.id}`);
      setApiKeys(apiKeys.filter(key => key.id !== selectedApiKey.id));
      setAlert({
        open: true,
        message: 'API key berhasil dihapus',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting API key:', error);
      setAlert({
        open: true,
        message: 'Gagal menghapus API key',
        severity: 'error'
      });
      handleCloseDialog();
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(newApiKey.key);
    setAlert({
      open: true,
      message: 'API key berhasil disalin ke clipboard',
      severity: 'success'
    });
  };

  const handleDoneWithApiKey = () => {
    setShowApiKey(false);
    setNewApiKey(null);
    handleCloseDialog();
  };

  const renderNotificationSettings = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <NotificationsIcon sx={{ mr: 2 }} />
        <Typography variant="h6">Pengaturan Notifikasi</Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.email}
                onChange={handleNotificationChange}
                name="email"
                color="primary"
              />
            }
            label="Email Notifications"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.push}
                onChange={handleNotificationChange}
                name="push"
                color="primary"
              />
            }
            label="Push Notifications"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.sms}
                onChange={handleNotificationChange}
                name="sms"
                color="primary"
              />
            }
            label="SMS Notifications"
          />
        </Grid>
        <Grid item xs={12}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveNotificationSettings}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Simpan Pengaturan'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderStorageSettings = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CloudIcon sx={{ mr: 2 }} />
        <Typography variant="h6">Pengaturan Penyimpanan</Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Penyedia Penyimpanan</InputLabel>
            <Select
              value={storageSettings.provider}
              onChange={handleStorageProviderChange}
              label="Penyedia Penyimpanan"
            >
              {storageProviders.map(provider => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {storageSettings.provider === 'google_drive' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client ID"
              variant="outlined"
              value={storageSettings.config.clientId || ''}
              onChange={(e) => setStorageSettings({
                ...storageSettings,
                config: { ...storageSettings.config, clientId: e.target.value }
              })}
            />
          </Grid>
        )}
        
        {storageSettings.provider === 'gcs' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project ID"
                variant="outlined"
                value={storageSettings.config.projectId || ''}
                onChange={(e) => setStorageSettings({
                  ...storageSettings,
                  config: { ...storageSettings.config, projectId: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bucket Name"
                variant="outlined"
                value={storageSettings.config.bucketName || ''}
                onChange={(e) => setStorageSettings({
                  ...storageSettings,
                  config: { ...storageSettings.config, bucketName: e.target.value }
                })}
              />
            </Grid>
          </>
        )}
        
        {storageSettings.provider === 's3' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bucket Name"
                variant="outlined"
                value={storageSettings.config.bucketName || ''}
                onChange={(e) => setStorageSettings({
                  ...storageSettings,
                  config: { ...storageSettings.config, bucketName: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Region"
                variant="outlined"
                value={storageSettings.config.region || ''}
                onChange={(e) => setStorageSettings({
                  ...storageSettings,
                  config: { ...storageSettings.config, region: e.target.value }
                })}
              />
            </Grid>
          </>
        )}
        
        <Grid item xs={12}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveStorageSettings}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Simpan Pengaturan'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderApiKeys = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ApiIcon sx={{ mr: 2 }} />
        <Typography variant="h6">API Keys</Typography>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleOpenAddApiKeyDialog}
        >
          Buat API Key Baru
        </Button>
      </Box>
      
      {apiKeys.length === 0 ? (
        <Typography variant="body1" sx={{ my: 3 }}>
          Belum ada API key yang dibuat. Klik tombol di atas untuk membuat API key baru.
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nama</TableCell>
                <TableCell>Izin</TableCell>
                <TableCell>Dibuat Pada</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell>{apiKey.name}</TableCell>
                  <TableCell>
                    {apiKey.permissions.map(perm => (
                      <Chip 
                        key={perm} 
                        label={apiPermissions.find(p => p.value === perm)?.label || perm} 
                        size="small" 
                        sx={{ mr: 1 }} 
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    {new Date(apiKey.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      color="error" 
                      onClick={() => handleOpenDeleteApiKeyDialog(apiKey)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Pengaturan Integrasi</Typography>
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Notifikasi" />
        <Tab label="Penyimpanan" />
        <Tab label="API Keys" />
      </Tabs>
      
      {tabValue === 0 && renderNotificationSettings()}
      {tabValue === 1 && renderStorageSettings()}
      {tabValue === 2 && renderApiKeys()}
      
      {/* Add API Key Dialog */}
      <Dialog open={openAddApiKeyDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {showApiKey ? 'API Key Berhasil Dibuat' : 'Buat API Key Baru'}
        </DialogTitle>
        <DialogContent>
          {showApiKey ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Salin API key Anda. API key ini hanya akan ditampilkan sekali.
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={newApiKey?.key || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyApiKey}>
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nama API Key"
                name="name"
                value={apiKeyFormData.name}
                onChange={handleInputChange}
                variant="outlined"
                sx={{ mb: 3 }}
              />
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Izin</InputLabel>
                <Select
                  multiple
                  value={apiKeyFormData.permissions}
                  onChange={handlePermissionChange}
                  label="Izin"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={apiPermissions.find(p => p.value === value)?.label || value} 
                          size="small" 
                        />
                      ))}
                    </Box>
                  )}
                >
                  {apiPermissions.map((permission) => (
                    <MenuItem key={permission.value} value={permission.value}>
                      {permission.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {showApiKey ? (
            <Button onClick={handleDoneWithApiKey} color="primary">
              Selesai
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Batal</Button>
              <Button 
                onClick={handleCreateApiKey} 
                color="primary" 
                disabled={dialogLoading || !apiKeyFormData.name}
              >
                {dialogLoading ? <CircularProgress size={24} /> : 'Buat'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Delete API Key Dialog */}
      <Dialog open={openDeleteApiKeyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hapus API Key</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Apakah Anda yakin ingin menghapus API key "{selectedApiKey?.name}"? Tindakan ini tidak dapat dibatalkan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleDeleteApiKey} 
            color="error" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationSettings;
